import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding, assetUrl } from "@/lib/branding";
import { api, fmtDate, formatApiError, API_URL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Upload, Trash2, Plus, KeyRound, UserPlus, ImageIcon, Check, Images, DatabaseBackup, Download, RotateCcw, HardDriveDownload, Clock, FileJson, FileSpreadsheet } from "lucide-react";
import { THEMES } from "@/components/Layout";
import { toast } from "sonner";

function ThemePicker() {
  const [current, setCurrent] = useState(localStorage.getItem("crm_theme") || "wave");
  const apply = (key) => {
    localStorage.setItem("crm_theme", key);
    setCurrent(key);
    window.dispatchEvent(new CustomEvent("crm-theme"));
    toast.success(`Theme: ${THEMES[key].name}`);
  };
  return (
    <Card className="p-6 border-slate-200 rounded-lg shadow-none" data-testid="theme-picker">
      <div className="text-sm font-semibold text-slate-800">Interface Theme</div>
      <div className="text-xs text-slate-500 mt-0.5">Applies instantly — pick the one that suits your brand.</div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(THEMES).map(([key, th]) => (
          <button key={key} onClick={() => apply(key)} data-testid={`theme-${key}`}
                  className={`text-left rounded-lg border-2 p-2 transition-colors duration-200 ${current === key ? "border-[#3B82F6]" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="h-14 rounded-md overflow-hidden flex border" style={{ borderColor: th.border }}>
              <div className="w-1/3 border-r" style={{ background: th.sidebar, borderColor: th.border }} />
              <div className="flex-1 flex flex-col" style={{ background: th.page }}>
                {th.darkHeader && <div className="h-3" style={{ background: "#3B82F6" }} />}
                <div className="m-1.5 flex-1 bg-white rounded-sm border border-slate-200" />
              </div>
            </div>
            <div className="mt-1.5 text-[11px] font-medium text-slate-700 flex items-center gap-1">
              {current === key && <Check size={11} className="text-[#3B82F6]" />} {th.name}
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
}

const ROLES = ["admin", "sales", "accounts", "production", "dispatch", "staff"];

function AssetCard({ kind, title, hint, url, onUploaded, darkPreview = false, extra = null }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image too large (max 4 MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/settings/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${title} updated`);
      onUploaded();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${title.toLowerCase()}?`)) return;
    try { await api.delete(`/settings/asset/${kind}`); toast.success(`${title} removed`); onUploaded(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card className="p-5 border-slate-200 rounded-lg shadow-none" data-testid={`asset-${kind}`}>
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
      <div className={`mt-4 h-28 rounded-lg border border-dashed border-slate-300 flex items-center justify-center overflow-hidden ${darkPreview ? "bg-[#3B82F6]" : "bg-slate-50"}`}>
        {url ? (
          <img src={url} alt={title} className="max-h-24 max-w-full object-contain px-3" />
        ) : (
          <div className="text-slate-400 flex flex-col items-center gap-1 text-xs"><ImageIcon size={20} strokeWidth={1.5} /> No image set</div>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <label className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-[#3B82F6] text-white cursor-pointer hover:bg-[#2563EB] transition-colors duration-200 ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload size={13} /> {busy ? "Uploading…" : url ? "Replace" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`upload-${kind}`} />
        </label>
        {url && (
          <button onClick={remove} className="text-xs px-3 py-2 rounded-md border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-200 transition-colors duration-200" data-testid={`remove-${kind}`}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
      {extra}
    </Card>
  );
}

function SlideTile({ slide, index, onChanged }) {
  const [title, setTitle] = useState(slide.title || "");
  const [caption, setCaption] = useState(slide.caption || "");
  const dirty = title !== (slide.title || "") || caption !== (slide.caption || "");
  const save = async () => {
    try { await api.patch(`/settings/slides/${slide.id}`, { title, caption }); toast.success("Caption saved"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this slideshow image?")) return;
    try { await api.delete(`/settings/slides/${slide.id}`); toast.success("Image removed"); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`slide-item-${slide.id}`}>
      <div className="relative group bg-[#3B82F6] aspect-[4/3]">
        <img src={assetUrl(slide.url)} alt="" className="w-full h-full object-cover" />
        <div className="absolute top-1.5 left-1.5 text-[10px] font-semibold bg-black/50 text-white rounded px-1.5 py-0.5">#{index + 1}</div>
        <button onClick={remove} data-testid={`remove-slide-${slide.id}`}
                className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-white/90 text-slate-500 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Trash2 size={13} />
        </button>
      </div>
      <div className="p-2.5 space-y-1.5 bg-slate-50/60">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Product name"
               className="h-8 text-xs bg-white" data-testid={`slide-title-${slide.id}`} />
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="One-liner (optional)"
               className="h-8 text-xs bg-white" data-testid={`slide-caption-${slide.id}`} />
        {dirty && (
          <button onClick={save} data-testid={`slide-save-${slide.id}`}
                  className="w-full text-[11px] font-medium px-2 py-1.5 rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors duration-200">
            Save Caption
          </button>
        )}
      </div>
    </div>
  );
}

function SlideshowCard() {
  const { branding, refresh } = useBranding();
  const [busy, setBusy] = useState(false);
  const slides = branding?.login_slides || [];
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const file of files) {
      if (file.size > 4 * 1024 * 1024) { toast.error(`${file.name}: too large (max 4 MB)`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/settings/slides", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) { toast.error(formatApiError(err)); }
    }
    toast.success("Slideshow updated");
    refresh();
    setBusy(false);
  };
  return (
    <Card className="p-6 border-slate-200 rounded-lg shadow-none" data-testid="login-slideshow-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Images size={15} className="text-[#FC5F5F]" /> Login Slideshow Images</div>
          <div className="text-xs text-slate-500 mt-0.5">Upload multiple product images — the login screens (staff & client portal) auto-rotate through them every 4 seconds, edge-to-edge. Add a product name & one-liner to show a caption on each slide. Max 10 images.</div>
        </div>
        <label className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-[#3B82F6] text-white cursor-pointer hover:bg-[#2563EB] transition-colors duration-200 ${busy || slides.length >= 10 ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload size={13} /> {busy ? "Uploading…" : "Add Images"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="upload-login-slides" />
        </label>
      </div>
      {slides.length ? (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {slides.map((s, i) => <SlideTile key={s.id} slide={s} index={i} onChanged={refresh} />)}
        </div>
      ) : (
        <div className="mt-4 h-24 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs text-slate-400 gap-1.5">
          <ImageIcon size={16} strokeWidth={1.5} /> No slideshow images yet — the single right-panel image below is used instead.
        </div>
      )}
    </Card>
  );
}

function PrefsCard() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { api.get("/settings/prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({})); }, []);
  const toggle = async () => {
    const next = !prefs?.payable_reminders;
    try {
      await api.put("/settings/prefs", { payable_reminders: next });
      setPrefs({ ...prefs, payable_reminders: next });
      toast.success(next ? "Payable reminders ON — overdue vendor bills (60+ days) will show on the dashboard" : "Payable reminders turned off");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="p-6 border-slate-200 rounded-lg shadow-none" data-testid="prefs-card">
      <div className="text-sm font-semibold text-slate-800 mb-1">Dashboard Alerts</div>
      <div className="flex items-center justify-between gap-4 mt-3">
        <div>
          <div className="text-[13px] font-medium text-slate-700">Payable reminders</div>
          <div className="text-xs text-slate-500">Show vendor bills that crossed the 60-day credit period on the dashboard.</div>
        </div>
        <button onClick={toggle} role="switch" aria-checked={!!prefs?.payable_reminders}
                className={`relative w-12 h-7 rounded-full transition-colors duration-200 flex-none ${prefs?.payable_reminders ? "bg-[#3B82F6]" : "bg-slate-300"}`}
                data-testid="pref-payable-reminders">
          <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all duration-200 ${prefs?.payable_reminders ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>
    </Card>
  );
}

function BrandingTab() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({ company_name: "", tagline: "", welcome_message: "" });
  useEffect(() => {
    if (branding) setForm({ company_name: branding.company_name || "", tagline: branding.tagline || "", welcome_message: branding.welcome_message || "" });
  }, [branding]);
  const save = async () => {
    try { await api.put("/settings/branding", { ...form, login_image_fit: branding?.login_image_fit || "cover" }); toast.success("Branding saved"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setImageFit = async (fit) => {
    try {
      await api.put("/settings/branding", { ...form, login_image_fit: fit });
      toast.success(fit === "contain" ? "Login image set to fit exactly (no crop)" : "Login image set to crop & fill");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6">
      <ThemePicker />
      <PrefsCard />
      <Card className="p-6 border-slate-200 rounded-lg shadow-none">
        <div className="text-sm font-semibold text-slate-800 mb-4">Identity</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Company Name</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5" data-testid="set-company-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Tagline</Label>
            <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1.5" data-testid="set-tagline" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Login Welcome Message</Label>
            <Input value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} className="mt-1.5" data-testid="set-welcome" />
          </div>
        </div>
        <Button onClick={save} className="mt-5 bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="set-branding-save">Save Branding</Button>
      </Card>
      <SlideshowCard />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AssetCard kind="sidebar_logo" title="Sidebar Logo" hint="Shown top-left of the menu (light themes)." url={assetUrl(branding?.sidebar_logo_url)} onUploaded={refresh} />
        <AssetCard kind="sidebar_logo_dark" title="Dark Theme Logo" hint="White/light logo version — used on dark sidebar themes." url={assetUrl(branding?.sidebar_logo_dark_url)} onUploaded={refresh} darkPreview />
        <AssetCard kind="sidebar_logo_collapsed" title="Collapsed Menu Logo" hint="Small square logo shown when the side menu is collapsed." url={assetUrl(branding?.sidebar_logo_collapsed_url)} onUploaded={refresh} />
        <AssetCard kind="login_logo" title="Login Screen Logo" hint="Shown above the sign-in form." url={assetUrl(branding?.login_logo_url)} onUploaded={refresh} />
        <AssetCard kind="login_image" title="Login Right-Panel Image" hint="Full-height image on the login page. Recommended ≈960×1080 px (portrait, 8:9 ratio)." url={assetUrl(branding?.login_image_url)} onUploaded={refresh}
          extra={
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Display Mode</div>
              <div className="flex gap-1.5 flex-wrap">
                {[["cover", "Crop to fill"], ["contain", "Fit exactly"], ["fill", "Stretch"]].map(([v, lab]) => (
                  <button key={v} onClick={() => setImageFit(v)}
                          className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors duration-200 ${(branding?.login_image_fit || "cover") === v ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                          data-testid={`login-image-fit-${v}`}>{lab}</button>
                ))}
              </div>
            </div>
          } />
      </div>
    </div>
  );
}

function ProfileTab() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const saveName = async () => {
    try {
      const r = await api.patch("/auth/profile", { name });
      setUser(r.data); toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const changePw = async () => {
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed"); setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 max-w-xl">
      <Card className="p-6 border-slate-200 rounded-lg shadow-none">
        <div className="text-sm font-semibold text-slate-800 mb-4">Your Profile</div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Full Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="profile-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label>
            <Input value={user?.email || ""} disabled className="mt-1.5 bg-slate-50" />
          </div>
          <Button onClick={saveName} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="profile-save">Save Profile</Button>
        </div>
      </Card>
      <Card className="p-6 border-slate-200 rounded-lg shadow-none">
        <div className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2"><KeyRound size={15} className="text-[#FC5F5F]" /> Change Password</div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Current Password</Label>
            <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="mt-1.5" data-testid="pw-current" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">New Password</Label>
              <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="mt-1.5" data-testid="pw-new" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Confirm New</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" data-testid="pw-confirm" />
            </div>
          </div>
          <Button onClick={changePw} disabled={!pw.current || !pw.next} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="pw-save">Update Password</Button>
        </div>
      </Card>
    </div>
  );
}

function UsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyU = { name: "", email: "", password: "", role: "staff" };
  const [form, setForm] = useState(emptyU);
  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try {
      await api.post("/auth/users", form);
      toast.success(`User ${form.email} added`); setOpen(false); setForm(emptyU); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setRole = async (u, role) => {
    try { await api.patch(`/auth/users/${u.id}`, { role }); toast.success(`${u.name} → ${role}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPw = async (u) => {
    const p = window.prompt(`New password for ${u.email}? (min 6 chars)`);
    if (!p) return;
    try { await api.patch(`/auth/users/${u.id}`, { password: p }); toast.success("Password reset"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Remove user ${u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(emptyU); setOpen(true); }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="user-add-btn">
          <UserPlus size={14} className="mr-1.5" /> Add User
        </Button>
      </div>
      <Card className="border-slate-200 rounded-lg shadow-none overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Added</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors duration-200" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{u.name}{u.id === user?.id && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FC5F5F]/10 text-[#C94A4A] border border-[#FC5F5F]/30 rounded px-1.5 py-0.5">You</span>}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => resetPw(u)} title="Reset password" className="p-1.5 text-slate-400 hover:text-[#FC5F5F] transition-colors duration-200" data-testid={`user-resetpw-${u.id}`}><KeyRound size={13} /></button>
                    {u.id !== user?.id && (
                      <button onClick={() => del(u)} title="Remove" className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors duration-200" data-testid={`user-del-${u.id}`}><Trash2 size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="new-user-email" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Password *</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="new-user-password" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!form.name || !form.email || !form.password} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="new-user-save"><Plus size={14} className="mr-1" /> Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BackupTab() {
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const [restoreFile, setRestoreFile] = useState(null);

  const loadCfg = () => api.get("/backup/config").then((r) => setCfg(r.data)).catch(() => {});
  const loadList = () => api.get("/backup/list").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); loadList(); }, []);

  const bytes = (n) => n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await api.get(`/backup/download?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `hitech-backup-${stamp}.${fmt === "excel" ? "xlsx" : "zip"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt === "excel" ? "Excel" : "JSON"} backup downloaded`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const runNow = async () => {
    setBusy("run");
    try { await api.post("/backup/run-now"); toast.success("Server backup created"); loadList(); loadCfg(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try { await api.put("/backup/config", { enabled: next.enabled, frequency: next.frequency, retention: next.retention }); toast.success("Backup schedule updated"); }
    catch (e) { toast.error(formatApiError(e)); loadCfg(); }
  };

  const downloadStored = async (b) => {
    try {
      const r = await api.get(`/backup/stored/${b.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = b.path.split("/").pop(); a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delStored = async (b) => {
    if (!window.confirm("Delete this backup permanently?")) return;
    try { await api.delete(`/backup/stored/${b.id}`); toast.success("Backup deleted"); loadList(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async () => {
    if (!restoreFile) return;
    if (!window.confirm("Restore will REPLACE all current data with the backup contents. This cannot be undone. Continue?")) return;
    setBusy("restore");
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const r = await api.post("/backup/restore?mode=replace", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const total = Object.values(r.data.restored || {}).reduce((s, n) => s + n, 0);
      toast.success(`Restored ${total} records — reloading…`);
      setRestoreFile(null);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="backup-tab">
      <Card className="p-6 border-slate-200 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2 mb-1">
          <DatabaseBackup size={18} className="text-[#3B82F6]" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Download a Backup</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">A complete snapshot of every module — customers, leads, quotations, orders, invoices, payments, products, vendors and more.</p>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => download("json")} disabled={busy === "json"} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="backup-download-json">
            <FileJson size={15} className="mr-2" /> {busy === "json" ? "Preparing…" : "Download JSON (.zip)"}
          </Button>
          <Button onClick={() => download("excel")} disabled={busy === "excel"} variant="outline" className="border-slate-300" data-testid="backup-download-excel">
            <FileSpreadsheet size={15} className="mr-2" /> {busy === "excel" ? "Preparing…" : "Download Excel (.xlsx)"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={18} className="text-[#3B82F6]" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Automatic Backups</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Backups run on the server on a schedule and are kept securely. Older backups beyond the retention limit are removed automatically.</p>
        {cfg && (
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-xs">Schedule</Label>
              <Select value={cfg.enabled ? cfg.frequency : "off"} onValueChange={(v) => saveCfg(v === "off" ? { enabled: false } : { enabled: true, frequency: v })}>
                <SelectTrigger className="mt-1.5" data-testid="backup-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Keep last</Label>
              <Select value={String(cfg.retention)} onValueChange={(v) => saveCfg({ retention: Number(v) })}>
                <SelectTrigger className="mt-1.5" data-testid="backup-retention"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 30, 60].map((n) => <SelectItem key={n} value={String(n)}>{n} backups</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={runNow} disabled={busy === "run"} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="backup-run-now">
              <HardDriveDownload size={15} className="mr-2" /> {busy === "run" ? "Backing up…" : "Back up now"}
            </Button>
          </div>
        )}
        {cfg?.last_auto_backup && <p className="text-xs text-slate-400 mt-3">Last automatic backup: {fmtDate(cfg.last_auto_backup)}</p>}
      </Card>

      <Card className="p-6 border-slate-200 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Stored Backups</h3>
        {list.length === 0 ? (
          <p className="text-sm text-slate-400">No server backups yet — click "Back up now" to create one.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="backup-list">
            {list.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-2.5" data-testid={`backup-row-${b.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{fmtDate(b.created_at)}
                    <span className={`ml-2 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${b.kind === "auto" ? "bg-sky-50 text-sky-600 border border-sky-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>{b.kind}</span>
                  </p>
                  <p className="text-xs text-slate-400">{b.total_records} records · {bytes(b.size)}</p>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => downloadStored(b)} title="Download" className="p-1.5 text-slate-400 hover:text-[#3B82F6] transition-colors duration-200" data-testid={`backup-download-${b.id}`}><Download size={14} /></button>
                  <button onClick={() => delStored(b)} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors duration-200" data-testid={`backup-del-${b.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 border-rose-200 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2 mb-1">
          <RotateCcw size={18} className="text-rose-600" />
          <h3 className="font-display text-lg font-semibold text-slate-900">Restore from Backup</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Upload a JSON backup (.json or .zip) to restore. <span className="text-rose-600 font-medium">This replaces all current data.</span></p>
        <div className="flex flex-wrap items-center gap-3">
          <Input type="file" accept=".json,.zip,application/json,application/zip" onChange={(e) => setRestoreFile(e.target.files?.[0] || null)} className="max-w-xs" data-testid="backup-restore-file" />
          <Button onClick={restore} disabled={!restoreFile || busy === "restore"} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="backup-restore-btn">
            <RotateCcw size={15} className="mr-2" /> {busy === "restore" ? "Restoring…" : "Restore"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function NumberingTab() {
  const [counters, setCounters] = useState([]);
  const [edits, setEdits] = useState({});
  const load = () => api.get("/backup/counters").then((r) => setCounters(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const LABELS = { "QUO-plain": "Quotation", "PI-plain": "Pro Forma Invoice", "TC-plain": "Test Certificate" };
  const label = (k) => LABELS[k] || (k.startsWith("INV-") ? `Tax Invoice (FY ${k.slice(4)})` : k);
  const nextPreview = (c, seq) => c.key.startsWith("INV-") ? `${String(seq + 1).padStart(3, "0")}/${c.key.slice(4)}` : String(seq + 1);
  const save = async (c) => {
    const next = parseInt(edits[c.key], 10);
    if (isNaN(next) || next < 1) { toast.error("Enter a valid next number"); return; }
    try {
      await api.post("/backup/set-counter", { key: c.key, seq: next - 1 });
      toast.success(`${label(c.key)} — next number will be ${nextPreview(c, next - 1)}`);
      setEdits((e) => ({ ...e, [c.key]: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="p-6 border-slate-200 rounded-lg shadow-none max-w-3xl" data-testid="numbering-card">
      <h3 className="font-semibold text-slate-800">Document Numbering</h3>
      <p className="text-xs text-slate-500 mt-1 mb-4">Set the next number for each document series. Changes take effect on the next document created.</p>
      <div className="space-y-3">
        {counters.map((c) => (
          <div key={c.key} className="flex flex-wrap items-center gap-3 border border-slate-100 rounded-md px-4 py-3" data-testid={`counter-row-${c.key}`}>
            <div className="min-w-[180px]">
              <div className="text-sm font-medium text-slate-800">{label(c.key)}</div>
              <div className="text-[11px] text-slate-400 font-mono">{c.key}</div>
            </div>
            <div className="text-sm text-slate-600">Next: <b className="font-mono" data-testid={`counter-next-${c.key}`}>{nextPreview(c, c.seq)}</b></div>
            <div className="flex items-center gap-2 ml-auto">
              <Input type="number" placeholder="New next number" value={edits[c.key] || ""}
                onChange={(e) => setEdits((ed) => ({ ...ed, [c.key]: e.target.value }))}
                className="w-40 h-9" data-testid={`counter-input-${c.key}`} />
              <Button size="sm" onClick={() => save(c)} disabled={!edits[c.key]} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid={`counter-save-${c.key}`}>Save</Button>
            </div>
          </div>
        ))}
        {counters.length === 0 && <p className="text-sm text-slate-500">No numbering counters yet — they appear after the first document of each type is created.</p>}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Branding, profile, users, data backup & access control" />
      <Tabs defaultValue={isAdmin ? "branding" : "profile"} className="w-full">
        <TabsList className="bg-slate-100">
          {isAdmin && <TabsTrigger value="branding" data-testid="settings-branding-tab">Branding</TabsTrigger>}
          <TabsTrigger value="profile" data-testid="settings-profile-tab">Profile</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="settings-users-tab">Users</TabsTrigger>}
          {isAdmin && <TabsTrigger value="backup" data-testid="settings-backup-tab">Backup</TabsTrigger>}
          {isAdmin && <TabsTrigger value="numbering" data-testid="settings-numbering-tab">Numbering</TabsTrigger>}
        </TabsList>
        {isAdmin && <TabsContent value="branding" className="mt-6"><BrandingTab /></TabsContent>}
        <TabsContent value="profile" className="mt-6"><ProfileTab /></TabsContent>
        {isAdmin && <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>}
        {isAdmin && <TabsContent value="backup" className="mt-6"><BackupTab /></TabsContent>}
        {isAdmin && <TabsContent value="numbering" className="mt-6"><NumberingTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
